import React, { useEffect, useMemo, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import NumberFormat from 'react-number-format';
import { Button, Container, Dimmer, Dropdown, Form, Icon, Label, Loader, Table } from 'semantic-ui-react';
import { formatDate, formatMongolianName } from '../../../utils/shared.utils';
import {
  fetchPapaDeliveriesReport,
  fetchPapaDistricts,
  isPapaDeliveryConfirmed,
  isPapaDeliveryCreated,
  PapaDeliveryRow,
  PapaDistrict,
} from '../order/order.papa-actions';
import './order.report.scss';

type PapaDeliveryGroup = {
  key: string
  papaId: string
  papaCode: string
  status: string
  papaDistrict: string
  error: string
  rows: PapaDeliveryRow[]
}

function formatDeliveryDate(value: string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return value
  }
  return `${date.getMonth() + 1} сарын ${date.getUTCDate()}`
}

function formatAddress(row: PapaDeliveryRow) {
  return [row.town, row.district, row.unit, row.street].filter(Boolean).join(', ')
}

function formatPapaCreatedAt(value: string) {
  return value ? formatDeliveryDate(value) : '-'
}

function formatPapaDistrict(value: string) {
  return value || '-'
}

function groupPapaDeliveries(deliveries: PapaDeliveryRow[]): PapaDeliveryGroup[] {
  const groups = new Map<string, PapaDeliveryGroup>()

  for (const row of deliveries) {
    const papaId = String(row.externalDeliveryId || '').trim()
    const key = papaId || `missing-${row.deliveryId}`
    const existing = groups.get(key)
    if (existing) {
      existing.rows.push(row)
      if (!existing.papaCode && row.externalDeliveryCode) {
        existing.papaCode = row.externalDeliveryCode
      }
      if (!existing.status && row.externalDeliveryStatus) {
        existing.status = row.externalDeliveryStatus
      }
      if (!existing.papaDistrict && row.papaDistrict) {
        existing.papaDistrict = row.papaDistrict
      }
      if (!existing.error && row.externalDeliveryError) {
        existing.error = row.externalDeliveryError
      }
      continue
    }

    groups.set(key, {
      key,
      papaId,
      papaCode: row.externalDeliveryCode || '',
      status: row.externalDeliveryStatus || '',
      papaDistrict: row.papaDistrict || '',
      error: row.externalDeliveryError || '',
      rows: [row],
    })
  }

  return Array.from(groups.values())
}

function ReportPapaDeliveries() {
  const history = useHistory()
  const [deliveries, setDeliveries] = useState<PapaDeliveryRow[]>([])
  const [loading, setLoading] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')

  const rangeStart = new Date()
  const rangeEnd = new Date()
  rangeStart.setMonth(rangeStart.getMonth() - 3)
  rangeEnd.setDate(rangeEnd.getDate() + 14)

  const [startDate, setStartDate] = useState(formatDate(rangeStart))
  const [endDate, setEndDate] = useState(formatDate(rangeEnd))
  const [district, setDistrict] = useState('')
  const [papaDistricts, setPapaDistricts] = useState<PapaDistrict[]>([])
  const [papaDistrictsLoading, setPapaDistrictsLoading] = useState(false)
  const [papaCreatedStartDate, setPapaCreatedStartDate] = useState('')
  const [papaCreatedEndDate, setPapaCreatedEndDate] = useState('')
  const [papaOrderId, setPapaOrderId] = useState('')

  useEffect(() => {
    const loadPapaDistricts = async () => {
      const token = localStorage.getItem('adminToken')
      if (!token || token === 'undefined') {
        return
      }

      setPapaDistrictsLoading(true)
      const result = await fetchPapaDistricts(process.env.REACT_APP_API_URL || '', token)
      setPapaDistrictsLoading(false)

      if (result.ok && result.districts) {
        setPapaDistricts(result.districts)
      }
    }

    loadPapaDistricts()
  }, [])

  const fetchReport = async () => {
    const token = localStorage.getItem('adminToken')
    if (!token || token === 'undefined') {
      window.alert('Та дахин нэвтэрнэ үү')
      history.push('/mongol/login')
      return
    }
    if (!startDate || !endDate) {
      setErrorMsg('Эхний болон сүүлийн өдрийг сонгоно уу')
      return
    }

    setLoading(true)
    setErrorMsg('')
    const result = await fetchPapaDeliveriesReport(
      process.env.REACT_APP_API_URL || '',
      token,
      {
        startDate,
        endDate,
        district: district.trim() || undefined,
        papaCreatedStartDate: papaCreatedStartDate || undefined,
        papaCreatedEndDate: papaCreatedEndDate || undefined,
        papaOrderId: papaOrderId.trim() || undefined,
      },
    )
    setLoading(false)

    if (!result.ok) {
      setErrorMsg(result.error || 'Papa хүргэлтийн тайлан авахад алдаа гарлаа')
      return
    }

    setDeliveries(result.deliveries || [])
  }

  useEffect(() => {
    fetchReport()
  }, [])

  const groups = useMemo(() => groupPapaDeliveries(deliveries), [deliveries])

  const summary = useMemo(() => {
    const totalDeliveries = deliveries.length
    const totalPapaOrders = groups.filter((group) => group.papaId).length
    const withError = deliveries.filter((row) => row.externalDeliveryError).length
    const confirmed = groups.filter((group) => group.papaId && isPapaDeliveryConfirmed({
      externalDeliveryStatus: group.status,
    })).length
    const pending = groups.filter((group) => (
      group.papaId
      && isPapaDeliveryCreated({ externalDeliveryId: group.papaId })
      && !isPapaDeliveryConfirmed({ externalDeliveryStatus: group.status })
    )).length
    const missingPapa = groups.filter((group) => !group.papaId).length
    return { totalDeliveries, totalPapaOrders, withError, confirmed, pending, missingPapa }
  }, [deliveries, groups])

  return (
    <div className="page-admin">
      <Container>
        <div className="flex-div width-100">
          <Dimmer active={loading} inverted>
            <Loader size="medium" />
          </Dimmer>
          <section className="admin-reports">
            <div className="width-100 text-center"><h2>Papa хүргэлтийн тайлан</h2></div>

            <Form className="report-filter flex-div margin-top-50">
              <Form.Input
                label="Эхний өдөр: "
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
              <Form.Input
                label="Сүүлийн өдөр: "
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
              <Form.Field>
                <label>Дүүрэг: </label>
                <Dropdown
                  data-testid="papa-report-district-filter"
                  placeholder="Дүүрэг сонгох"
                  selection
                  search
                  clearable
                  loading={papaDistrictsLoading}
                  options={papaDistricts.map((papaDistrictOption) => ({
                    key: papaDistrictOption.id,
                    value: papaDistrictOption.name,
                    text: papaDistrictOption.short_name ? `${papaDistrictOption.name} (${papaDistrictOption.short_name})` : papaDistrictOption.name,
                  }))}
                  value={district || undefined}
                  onChange={(_e, data) => setDistrict(String(data.value || ''))}
                />
              </Form.Field>
              <Form.Input
                data-testid="papa-report-papa-created-start-filter"
                label="Papa үүссэн эхлэл: "
                type="date"
                value={papaCreatedStartDate}
                onChange={(e) => setPapaCreatedStartDate(e.target.value)}
              />
              <Form.Input
                data-testid="papa-report-papa-created-end-filter"
                label="Papa үүссэн төгсгөл: "
                type="date"
                value={papaCreatedEndDate}
                onChange={(e) => setPapaCreatedEndDate(e.target.value)}
              />
              <Form.Input
                data-testid="papa-report-papa-order-id-filter"
                label="Papa захиалгын ID: "
                placeholder="Papa захиалгын ID"
                value={papaOrderId}
                onChange={(e) => setPapaOrderId(e.target.value)}
              />
              <Form.Field className="report-filter-actions">
                <label>&nbsp;</label>
                <Button data-testid="papa-report-search" type="button" primary onClick={fetchReport}>Хайх</Button>
              </Form.Field>
            </Form>

            {errorMsg ? <div className="error-msg margin-top-20">{errorMsg}</div> : null}

            <div className="margin-top-20">
              <Label>Papa захиалга: {summary.totalPapaOrders}</Label>{' '}
              <Label>Хүргэлт: {summary.totalDeliveries}</Label>{' '}
              <Label color="orange">Батлаагүй: {summary.pending}</Label>{' '}
              <Label color="green">Баталсан: {summary.confirmed}</Label>{' '}
              <Label color="red">Алдаатай: {summary.withError}</Label>{' '}
              <Label color="grey">Papa ID байхгүй: {summary.missingPapa}</Label>
            </div>

            <Table celled striped className="admin-list-table margin-top-20">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Papa захиалга</Table.HeaderCell>
                  <Table.HeaderCell>Төлөв</Table.HeaderCell>
                  <Table.HeaderCell>Хүргэлт / Захиалга</Table.HeaderCell>
                  <Table.HeaderCell>Papa үнэ</Table.HeaderCell>
                  <Table.HeaderCell>Захиалгын огноо</Table.HeaderCell>
                  <Table.HeaderCell>Papa хүргэлт үүссэн огноо</Table.HeaderCell>
                  <Table.HeaderCell>Дүүрэг</Table.HeaderCell>
                  <Table.HeaderCell>Хэрэглэгч</Table.HeaderCell>
                  <Table.HeaderCell>Хаяг</Table.HeaderCell>
                  <Table.HeaderCell>Алдаа</Table.HeaderCell>
                  <Table.HeaderCell>Дэлгэрэнгүй</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {groups.map((group) => {
                  const papaLabel = group.papaCode || group.papaId || 'Papa ID байхгүй'
                  return (
                    <Table.Row key={group.key} error={!!group.error || group.rows.some((row) => row.externalDeliveryError)}>
                      <Table.Cell data-label="Papa захиалга">
                        <strong>{papaLabel}</strong>
                        {group.papaId && group.papaCode ? <div style={{ color: 'var(--admin-text-muted)', fontSize: '0.9em' }}>{group.papaId}</div> : null}
                        <div>{group.rows.length} хүргэлт</div>
                      </Table.Cell>
                      <Table.Cell data-label="Төлөв">{group.status || '-'}</Table.Cell>
                      <Table.Cell data-label="Хүргэлт / Захиалга">
                        {group.rows.map((row) => (
                          <div key={row.deliveryId}>
                            #{row.deliveryId}{' / '}
                            <Link className="underline cursor" to={`/mongol/report/order/${row.orderId}`} target="_blank">
                              <strong>#{row.orderId}</strong>
                            </Link>
                          </div>
                        ))}
                      </Table.Cell>
                      <Table.Cell data-label="Papa үнэ">
                        {group.rows.map((row) => (
                          <div key={`papa-price-${row.deliveryId}`}>
                            <NumberFormat displayType="text" value={row.papaPrice} suffix="₮" thousandSeparator={true} />
                          </div>
                        ))}
                      </Table.Cell>
                      <Table.Cell data-label="Захиалгын огноо">
                        {group.rows.map((row) => (
                          <div key={`date-${row.deliveryId}`}>{formatDeliveryDate(row.date)}</div>
                        ))}
                      </Table.Cell>
                      <Table.Cell data-label="Papa хүргэлт үүссэн огноо">
                        {group.rows.map((row) => (
                          <div key={`papa-created-${row.deliveryId}`}>{formatPapaCreatedAt(row.papaCreatedAt)}</div>
                        ))}
                      </Table.Cell>
                      <Table.Cell data-label="Дүүрэг">{formatPapaDistrict(group.papaDistrict)}</Table.Cell>
                      <Table.Cell data-label="Хэрэглэгч">
                        {group.rows.map((row) => (
                          <div key={`user-${row.deliveryId}`}>
                            {formatMongolianName(row.parentName, row.ownName)}
                            {row.companyName ? <div>{row.companyName}</div> : null}
                          </div>
                        ))}
                      </Table.Cell>
                      <Table.Cell data-label="Хаяг">
                        {group.rows.map((row) => (
                          <div key={`address-${row.deliveryId}`}>{formatAddress(row)}</div>
                        ))}
                      </Table.Cell>
                      <Table.Cell data-label="Алдаа">
                        {group.rows.map((row) => (
                          <div key={`error-${row.deliveryId}`}>{row.externalDeliveryError || '-'}</div>
                        ))}
                      </Table.Cell>
                      <Table.Cell className="cursor" data-label="Дэлгэрэнгүй">
                        {group.rows.map((row) => (
                          <div key={`detail-${row.deliveryId}`}>
                            <Link to={`/mongol/report/order/${row.orderId}`} target="_blank" title="Захиалгын дэлгэрэнгүй">
                              <strong><Icon name="folder open outline" /></strong>
                            </Link>
                          </div>
                        ))}
                      </Table.Cell>
                    </Table.Row>
                  )
                })}
              </Table.Body>
            </Table>
          </section>
        </div>
      </Container>
    </div>
  )
}

export default ReportPapaDeliveries
